import React, { useRef } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, Table, Badge } from "reactstrap";

const ProjectSummaryModal = ({ isOpen, toggle, project, client, assignedMembers = [], workLogs = [] }) => {
  const printRef = useRef();

  if (!project) return null;

  const projectBudget = parseFloat(project?.budget || 0);
  const payments = Array.isArray(project?.payment) ? project.payment : [];
  const totalPaid = payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  const remainingDue = Math.max(0, projectBudget - totalPaid);
  const paymentCount = payments.length;
  const devProgressData = project?.developer_progress || {};

  const handlePrint = () => {
    window.print();
  };

  const getPriorityText = priority => {
    const priorityMap = { 1: "High", 2: "Medium", 3: "Low" };
    return priorityMap[priority] || "Not Set";
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" className="project-summary-modal">
      <style>
        {`
          @media print {
            @page {
              size: A4 portrait;
              margin: 10mm;
            }
            body {
              background: #ffffff !important;
              color: #000000 !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            /* Hide main App root and backdrop overlay */
            #root, .modal-backdrop {
              display: none !important;
            }
            .modal {
              position: static !important;
              display: block !important;
              overflow: visible !important;
              width: 100% !important;
              height: auto !important;
              padding: 0 !important;
              margin: 0 !important;
            }
            .modal-dialog {
              max-width: 100% !important;
              width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            .modal-content {
              border: none !important;
              box-shadow: none !important;
              padding: 0 !important;
              background: #ffffff !important;
            }
            .no-print {
              display: none !important;
            }
          }
        `}
      </style>

      <ModalHeader toggle={toggle} className="no-print border-bottom">
        <i className="fas fa-file-invoice me-2 text-primary"></i>
        Project Summary PDF Report
      </ModalHeader>

      <ModalBody className="project-summary-modal-content p-4">
        {/* Company & Document Header */}
        <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
          <div>
            <h3 className="text-primary fw-bold mb-1">Softzen IT</h3>
            <p className="text-muted small mb-0">Project Summary & Financial Statement</p>
          </div>
          <div className="text-end">
            <Badge color="primary" className="fs-6 px-3 py-2">
              Status: {project.status || "Pending"}
            </Badge>
            <div className="text-muted fs-12 mt-1">
              Date: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
            </div>
          </div>
        </div>

        {/* Project Basic Info */}
        <Row className="mb-4">
          <Col md={6}>
            <div className="p-3 bg-light rounded">
              <h6 className="text-primary fw-bold mb-2">Project Information</h6>
              <div className="fs-13 mb-1"><strong>Project Name:</strong> {project.name}</div>
              <div className="fs-13 mb-1"><strong>Client Name:</strong> {client?.name || client?.nickname || "N/A"}</div>
              <div className="fs-13 mb-1"><strong>Client Email:</strong> {client?.personal_email || client?.email || "N/A"}</div>
              <div className="fs-13 mb-1"><strong>Priority:</strong> {getPriorityText(project.priority_status)}</div>
              <div className="fs-13"><strong>Overall Progress:</strong> {project.progress || 0}%</div>
            </div>
          </Col>
          <Col md={6}>
            <div className="p-3 bg-light rounded">
              <h6 className="text-primary fw-bold mb-2">Timeline Details</h6>
              <div className="fs-13 mb-1"><strong>Start Date:</strong> {project.start_date || "Not Set"}</div>
              <div className="fs-13 mb-1"><strong>End Date:</strong> {project.end_date || "Not Set"}</div>
              <div className="fs-13 mb-1">
                <strong>Marketing Officer:</strong> {
                  assignedMembers.find(m => m.role === 'Marketing Officer')?.name || "N/A"
                }
              </div>
              <div className="fs-13 mb-1"><strong>Total Team Assigned:</strong> {assignedMembers.length} Members</div>
              <div className="fs-13"><strong>Total Work Logs Recorded:</strong> {workLogs.length} Entries</div>
            </div>
          </Col>
        </Row>

        {/* Financial Summary Cards */}
        <h6 className="fw-bold text-dark mb-3">
          <i className="fas fa-wallet me-2 text-primary"></i>Financial Summary
        </h6>
        <Row className="g-3 mb-4">
          <Col md={3}>
            <div className="p-3 border rounded text-center bg-light">
              <span className="text-muted fs-12 d-block mb-1">Project Value (Budget)</span>
              <h5 className="fw-bold text-primary mb-0">৳ {projectBudget.toLocaleString()}</h5>
            </div>
          </Col>
          <Col md={3}>
            <div className="p-3 border rounded text-center bg-light">
              <span className="text-muted fs-12 d-block mb-1">Total Paid (Received)</span>
              <h5 className="fw-bold text-success mb-0">৳ {totalPaid.toLocaleString()}</h5>
            </div>
          </Col>
          <Col md={3}>
            <div className="p-3 border rounded text-center bg-light">
              <span className="text-muted fs-12 d-block mb-1">Total Due (Remaining)</span>
              <h5 className={`fw-bold mb-0 ${remainingDue > 0 ? "text-danger" : "text-success"}`}>
                ৳ {remainingDue.toLocaleString()}
              </h5>
            </div>
          </Col>
          <Col md={3}>
            <div className="p-3 border rounded text-center bg-light">
              <span className="text-muted fs-12 d-block mb-1">Payment Installments</span>
              <h5 className="fw-bold text-dark mb-0">{paymentCount} Times</h5>
            </div>
          </Col>
        </Row>

        {/* Scheduled Payments & Invoice Sending Dates */}
        <div className="mb-4">
          <h6 className="fw-bold text-dark mb-2">
            <i className="fas fa-file-invoice-dollar me-2 text-primary"></i>Planned Payment Schedule & Invoice Send Dates
          </h6>
          <Table className="table-bordered align-middle fs-13 mb-0">
            <thead className="table-light">
              <tr>
                <th>Installment</th>
                <th>Payment Due Date</th>
                <th>Planned Amount</th>
                <th>Invoice Send Date</th>
                <th>Payment Status</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="fw-bold text-primary">1st Payment</td>
                <td>{project.first_payment_date || "Not Set"}</td>
                <td className="fw-semibold">৳ {parseFloat(project.first_payment_amount || 0).toLocaleString()}</td>
                <td className="fw-bold text-dark">{project.first_payment_invoice_date || "Not Set"}</td>
                <td>
                  <Badge color={project.first_payment_status === "Paid" ? "success" : project.first_payment_status === "Invoice Sent" ? "info" : "warning"}>
                    {project.first_payment_status || "Pending"}
                  </Badge>
                </td>
              </tr>
              <tr>
                <td className="fw-bold text-primary">2nd Payment</td>
                <td>{project.second_payment_date || "Not Set"}</td>
                <td className="fw-semibold">৳ {parseFloat(project.second_payment_amount || 0).toLocaleString()}</td>
                <td className="fw-bold text-dark">{project.second_payment_invoice_date || "Not Set"}</td>
                <td>
                  <Badge color={project.second_payment_status === "Paid" ? "success" : project.second_payment_status === "Invoice Sent" ? "info" : "warning"}>
                    {project.second_payment_status || "Pending"}
                  </Badge>
                </td>
              </tr>
            </tbody>
          </Table>
        </div>

        {/* Google Calendar Scheduled Meeting Info */}
        {(project.google_calendar_meeting_link || project.google_calendar_meeting_date) && (
          <div className="mb-4 p-3 border rounded bg-light">
            <h6 className="fw-bold text-success mb-2">
              <i className="fab fa-google me-2"></i>Google Calendar Scheduled Meeting
            </h6>
            <Row className="fs-13">
              <Col md={6}>
                <strong>Meeting Date & Time:</strong> {project.google_calendar_meeting_date ? new Date(project.google_calendar_meeting_date).toLocaleString() : "Not Scheduled"}
              </Col>
              <Col md={6}>
                <strong>Meeting Link:</strong>{" "}
                {project.google_calendar_meeting_link ? (
                  <a href={project.google_calendar_meeting_link} target="_blank" rel="noreferrer" className="text-primary text-decoration-underline ms-1">
                    {project.google_calendar_meeting_link}
                  </a>
                ) : (
                  "N/A"
                )}
              </Col>
            </Row>
          </div>
        )}

        {/* Payment Installments Table */}
        <div className="mb-4">
          <h6 className="fw-bold text-dark mb-2">
            <i className="fas fa-list-alt me-2 text-primary"></i>Payment Transactions History ({paymentCount} Paid)
          </h6>
          <Table className="table-bordered align-middle fs-13 mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: "60px" }}>#</th>
                <th>Payment Date</th>
                <th>Transaction Amount (BDT)</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {payments.length > 0 ? (
                payments.map((p, idx) => (
                  <tr key={p.id || idx}>
                    <td>{idx + 1}</td>
                    <td>{p.date || "N/A"}</td>
                    <td className="fw-semibold text-success">৳ {Number(parseFloat(p.amount || 0).toFixed(0)).toLocaleString()}</td>
                    <td><Badge color="success">Received</Badge></td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center text-muted py-3">No payments recorded yet.</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {/* Team Assignments Table */}
        <div className="mb-3">
          <h6 className="fw-bold text-dark mb-2">
            <i className="fas fa-users me-2 text-primary"></i>Assigned Team Members ({assignedMembers.length} Members)
          </h6>
          <Table className="table-bordered align-middle fs-13 mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: "60px" }}>#</th>
                <th>Member Name</th>
                <th>Assigned Role</th>
                <th>Individual Progress</th>
                <th>Budget Contribution Share</th>
              </tr>
            </thead>
            <tbody>
              {assignedMembers.length > 0 ? (
                (() => {
                  const techMembers = assignedMembers.filter(m =>
                    m.role !== 'Marketing Officer' &&
                    !m.role?.toLowerCase().includes('marketing')
                  );
                  const techCount = techMembers.length || 1;
                  const techMemberShare = projectBudget / techCount;

                  return assignedMembers.map((member, idx) => {
                    const isMarketing = member.role === 'Marketing Officer' || member.role?.toLowerCase().includes('marketing');
                    const devVal = devProgressData[member.id] ?? devProgressData[String(member.id)] ?? 0;
                    const memberShareText = isMarketing ? "Separate Sales Commission" : `৳ ${Math.round(techMemberShare).toLocaleString()}`;

                    return (
                      <tr key={member.id}>
                        <td>{idx + 1}</td>
                        <td className="fw-semibold">{member.name}</td>
                        <td><Badge color={isMarketing ? "success" : "info"}>{member.role}</Badge></td>
                        <td>{isMarketing ? "N/A" : `${devVal}%`}</td>
                        <td className="fw-semibold text-dark">{memberShareText}</td>
                      </tr>
                    );
                  });
                })()
              ) : (
                <tr>
                  <td colSpan="5" className="text-center text-muted py-3">No team members assigned yet.</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {/* Footer Note for Print */}
        <div className="mt-4 pt-3 border-top text-center text-muted fs-12">
          This document is an official project summary generated by Softzen IT System.
        </div>
      </ModalBody>

      <ModalFooter className="no-print">
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
        <Button color="primary" onClick={handlePrint}>
          <i className="fas fa-print me-1"></i> Print / Download PDF
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ProjectSummaryModal;
